import {
  Button,
  ButtonColor,
  IconCalendar,
  IconChevronDown,
  Menu,
  ToastDuration,
  ToastType,
  useToast,
} from '@leland-dev/leland-ui-library';
import { type FC } from 'react';

import { type Scalars } from '../../__generated-gql-types__/globalTypes';
import { downloadFileFromString } from '../../utils/file';

import { useAddToCalendarButtonCoachingSessionCalendarInviteLazyQuery } from './__generated-gql-types__/AddToCalendarButton.generated';

interface AddToCalendarButtonProps {
  scheduledSessionIds: Array<Scalars['CoachingSessionUrn']['output']>;
  label?: string;
  email?: string;
  justText?: boolean;
}

export const AddToCalendarButton: FC<AddToCalendarButtonProps> = ({
  scheduledSessionIds,
  label,
  email,
  justText,
}) => {
  const { showToast } = useToast();
  const [getCalendarInviteICS] =
    useAddToCalendarButtonCoachingSessionCalendarInviteLazyQuery();
  const [getCalendarInvite] =
    useAddToCalendarButtonCoachingSessionCalendarInviteLazyQuery();
  const handleICS = async () => {
    try {
      for (const scheduledSessionId of scheduledSessionIds) {
        const { data } = await getCalendarInviteICS({
          variables: { id: scheduledSessionId, createIcsFile: true, email },
        });

        if (data?.coachingSessionCalendarInvite) {
          downloadFileFromString(
            Buffer.from(
              data.coachingSessionCalendarInvite,
              'base64',
            ).toString(),
            'invite.ics',
            'text/calendar',
          );
        } else {
          showToast({
            type: ToastType.ERROR,
            duration: ToastDuration.NORMAL,
            message: 'Failed to download ICS file.',
          });
        }
      }
    } catch (e) {
      console.warn(e);
    }
  };

  const handleGCal = async () => {
    if (scheduledSessionIds.length !== 1) {
      showToast({
        type: ToastType.ERROR,
        duration: ToastDuration.NORMAL,
        message: 'Failed to add to Google Calendar.',
      });
      return;
    }

    try {
      const { data: gCalLink, error } = await getCalendarInvite({
        variables: { id: scheduledSessionIds[0], createIcsFile: false, email },
      });

      if (error) {
        showToast({
          type: ToastType.ERROR,
          duration: ToastDuration.NORMAL,
          message: 'Failed to add to Google Calendar.',
        });
        return;
      }

      window.open(
        gCalLink?.coachingSessionCalendarInvite,
        '_blank',
        'noopener,noreferrer',
      );
    } catch (e) {
      console.warn(e);
    }
  };
  return (
    <Menu
      itemSections={[
        [
          ...(scheduledSessionIds.length === 1
            ? [
                {
                  label: 'Add to Google Calendar',
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  onSelect: handleGCal,
                },
              ]
            : []),
          {
            label: 'Add to iCal/Outlook',
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSelect: handleICS,
          },
        ],
      ]}
    >
      {justText ? (
        <div className="flex items-center gap-1 leading-none text-gray-400">
          Add to Calendar{' '}
          <IconChevronDown className="size-5 text-leland-gray-light" />
        </div>
      ) : (
        <Button
          label={label ?? 'Add to Calendar'}
          LeftIcon={IconCalendar}
          buttonColor={ButtonColor.WHITE}
          RightIcon={IconChevronDown}
        />
      )}
    </Menu>
  );
};
